/* eslint-disable no-shadow */
// Bare package (not plugins/axios) purely for isCancel(), so the store does
// not take on the plugin's import of the store itself.
import axios from "axios";
import commonHelper from "../helpers/common";
import cartService from "../service/cart.service";
import productService from "../service/product.service";
import OrderJson from "../dataTypes/v2/order";
import i18n from "../i18n";
import { findBlockedArticles, isCartLineBlocked } from "../helpers/availability";

// The cart check must never be the reason an order cannot be placed: past
// this, or on any failure to read the menu, the order goes through (POSHUB
// still records a warning for an out-of-window line).
const AVAILABILITY_CHECK_TIMEOUT_MS = 5000;

const NO_BLOCKED_ARTICLES = { products: [], options: [] };

let isPreview = false;
try {
  isPreview = window.location.search.includes('preview=true');
} catch (e) {
  // window may not exist in all environments
}

const previewProducts = [
  {
    id: 1,
    name: "Margherita Pizza",
    totalPrice: 12.95,
    quantity: 2,
    optionalGroups: [{ name: "Extra kaas", price: 1.50 }],
    remarks: "",
  },
  {
    id: 2,
    name: "Tiramisu",
    totalPrice: 6.50,
    quantity: 1,
    optionalGroups: [],
    remarks: "",
  },
];

// Only a 400 genuinely means the session expired. A request that never reached
// the server has no response at all, and must not be reported as an expired
// session -- that would send staff and guests chasing the wrong problem.
const SESSION_EXPIRED_MESSAGE =
  "Sorry, your order failed to create. session has been expired, please contact our staff!";
const VALIDATION_ERROR_MESSAGE = "Create order failed, due to validation is error!";
// The order request may well have reached the server even though the reply did
// not come back, so this deliberately does not invite an immediate retry.
const CONNECTION_ERROR_MESSAGE =
  "Connection problem. Your order may already have been received - please check with our staff before ordering again.";
const GENERIC_ERROR_MESSAGE =
  "Sorry, something went wrong while placing your order. Please contact our staff!";

// Pulls the pieces a catch block needs without ever reading a property of
// something that may be undefined. A cancelled or network-level error carries
// no `response`, which is what used to throw a TypeError inside the catch and
// stop the awaiting component from ever running its post-dispatch code.
function describeError(e) {
  const response = e && e.response;
  const data = response && response.data;
  return {
    hasResponse: Boolean(response),
    status: response && response.status,
    errors: data && data.errors,
  };
}

// Chooses the snackbar copy for a failure that is not a cancellation.
function errorMessageFor({ hasResponse, status }) {
  if (status == 400) return SESSION_EXPIRED_MESSAGE;
  if (status == 422) return VALIDATION_ERROR_MESSAGE;
  if (!hasResponse) return CONNECTION_ERROR_MESSAGE;
  return GENERIC_ERROR_MESSAGE;
}

const state = {
  isLoading: false,
  data: {
    order_remark: "",
  },
  order: {},
  orderId: null,
  products: isPreview ? previewProducts : [],
  deliveryCost: 0,
  tableId: isPreview ? "5" : "",
  shopId: process.env.VUE_APP_SHOP_ID,
  remarks: "",
  paymentMethod: "cash",
  paymentMethodOptions: {},
  isOrderLoading: false,
  isError: false,
  errorData: {},
  customer: {},
  requested_time: "",
  // Article keys found unorderable by the last checkCartAvailability(). The
  // lines themselves are derived (getters.blockedLines), so removing a line
  // clears its flag without another request.
  blockedArticles: NO_BLOCKED_ARTICLES,
};

const actions = {
  clearAll({ commit }) {
    commit("SET_ORDER_DATA", {});
    commit("SET_ORDER_ID", "");
    commit("CLEAR_CART");
    commit("SET_ORDER_LOADING", false);
    commit("SET_TABLE", "");
    commit("SET_ERROR", false);
    commit("SET_ERROR_DATA", {});
  },

  setOrderData({ commit }, payload) {
    commit("SET_DATA", payload);
  },

  async getOrderById({ commit, getters }, orderId) {
    const result = await cartService.getOrderById(orderId);
    commit("SET_ORDER_DATA", result.data);
  },

  /**
   * Re-read the menu and flag cart lines that cannot be ordered right now
   * (Smart Menu): a product or option whose article has no available visible
   * row. Resolves true when ordering may go ahead -- including every failure
   * to read the menu (no menu id, network error, timeout, unexpected body):
   * this check fails open.
   */
  async checkCartAvailability({ commit, state, rootGetters }) {
    const themeMenu = rootGetters["theme/menu"];
    const menuId = themeMenu && themeMenu.categoryBlock && themeMenu.categoryBlock.data
      ? themeMenu.categoryBlock.data.menuId
      : null;
    if (!menuId || !state.products || state.products.length === 0) {
      commit("SET_BLOCKED_ARTICLES", NO_BLOCKED_ARTICLES);
      return true;
    }

    let response;
    try {
      response = await productService.getGroupedSilent(
        { menu_id: menuId },
        { timeout: AVAILABILITY_CHECK_TIMEOUT_MS }
      );
    } catch (e) {
      console.warn("cart: availability check skipped", e);
      commit("SET_BLOCKED_ARTICLES", NO_BLOCKED_ARTICLES);
      return true;
    }
    if (!response || !Array.isArray(response.data)) {
      commit("SET_BLOCKED_ARTICLES", NO_BLOCKED_ARTICLES);
      return true;
    }

    const blocked = findBlockedArticles(state.products, response.data);
    commit("SET_BLOCKED_ARTICLES", blocked);
    return blocked.products.length === 0 && blocked.options.length === 0;
  },

  // Shared by the order actions: false (and the message) when the cart has a
  // line that cannot be ordered now.
  async guardCartAvailability({ commit, dispatch }) {
    const ok = await dispatch("checkCartAvailability");
    if (!ok) {
      commit("SET_ERROR", true);
      commit("snackbar/error", i18n.t("availability.cartBlocked"), { root: true });
    }
    return ok;
  },

  removeBlockedLines({ commit }) {
    commit("REMOVE_BLOCKED_LINES");
  },

  removeLine({ commit }, index) {
    commit("REMOVE_LINE", index);
  },

  async createPickupOrder({ commit, getters, dispatch }, payload) {
    if (!(await dispatch("guardCartAvailability"))) {
      return { blocked: true };
    }
    try {
      commit("SET_ORDER_LOADING", true);
      const result = await cartService.createOrder(payload);

      if (result && result.data) {
        commit("SET_ORDER_DATA", result.data);
        commit("CLEAR_CART");
        commit("SET_ORDER_LOADING", false);
        commit("SET_ERROR", false);
        commit("SET_ERROR_DATA", {});
      }
      return result;
    } catch (e) {
      // A cancelled request means a newer order request superseded this one.
      // It is not an order failure, so no error state and no snackbar; the
      // newer request owns the outcome and clears the loading flag, which
      // also keeps the button disabled instead of inviting another tap.
      if (axios.isCancel(e)) {
        return null;
      }
      commit("SET_ERROR", true);
      commit("SET_ORDER_LOADING", false);
      console.error("cart: order request failed", e);
      const described = describeError(e);
      if (described.errors) {
        commit("SET_ERROR_DATA", described.errors);
      }
      commit("snackbar/error", errorMessageFor(described), {
        root: true,
      });
      return null;
    }
  },

  async createOrder({ commit, getters, dispatch }) {
    // Before CLEAR_CART below, or a blocked cart would be emptied unordered.
    if (!(await dispatch("guardCartAvailability"))) {
      return { blocked: true };
    }
    const bodyPayload = {
      ...getters.getOrderPayload,
    };

    if (this.state.session.data && this.state.session.data.id) {
      bodyPayload.session_id = this.state.session.data.id;
      bodyPayload.is_round = false;
    }

    try {
      commit("SET_ORDER_LOADING", true);
      commit("CLEAR_CART");
      const result = await cartService.createOrder(bodyPayload);
      commit("SET_ORDER_LOADING", false);
      commit("SET_ERROR", false);
      if (result && result.data) {
        commit("SET_ORDER_DATA", result.data);
      }
      // commit("session/CLEAR", null, { root: true });
      return result;
    } catch (e) {
      // A cancelled request means a newer order request superseded this one.
      // It is not an order failure, so no error state and no snackbar; the
      // newer request owns the outcome and clears the loading flag, which
      // also keeps the button disabled instead of inviting another tap.
      if (axios.isCancel(e)) {
        return null;
      }
      commit("SET_ERROR", true);
      commit("SET_ORDER_LOADING", false);
      console.error("cart: order request failed", e);
      const described = describeError(e);
      if (described.errors) {
        commit("SET_ERROR_DATA", described.errors);
      }
      commit("snackbar/error", errorMessageFor(described), {
        root: true,
      });
      return null;
    }
  },

  async makePayment({ commit, getters, dispatch, state }, payload) {
    try {
      commit("SET_ORDER_LOADING", true);
      commit("CLEAR_CART");
      const result = await cartService.createPayment(payload);
      // commit("session/CLEAR", null, { root: true });

      if (getters.getOrder && getters.getOrder.id) {
        await dispatch("getOrderById", getters.getOrder.id);
      }
      commit("SET_ORDER_LOADING", false);
      commit("SET_ERROR", false);
      return result;
    } catch (e) {
      // A cancelled request means a newer order request superseded this one.
      // It is not an order failure, so no error state and no snackbar; the
      // newer request owns the outcome and clears the loading flag, which
      // also keeps the button disabled instead of inviting another tap.
      if (axios.isCancel(e)) {
        return null;
      }
      commit("SET_ERROR", true);
      commit("SET_ORDER_LOADING", false);
      console.error("cart: order request failed", e);
      const described = describeError(e);
      if (described.errors) {
        commit("SET_ERROR_DATA", described.errors);
      }
      commit("snackbar/error", errorMessageFor(described), {
        root: true,
      });
      return null;
    }
  },

  async createOrderPayload({ commit, getters, rootGetters }) {
    const bodyPayload = {
      ...getters.getOrderPayloadWithoutPayment,
    };
    if (rootGetters["theme/isRestaurantEnabled"]) {
      bodyPayload["order_source_code"] =
        rootGetters["theme/general"].appSetting.restaurantOrderSourceCode;
      bodyPayload["order_type_code"] =
        rootGetters["theme/general"].appSetting.restaurantOrderTypeCode;
    } else {
      bodyPayload["order_source_code"] =
        rootGetters["theme/general"].appSetting.pickupOrderSourceCode;
      bodyPayload["order_type_code"] = rootGetters["theme/general"].appSetting.pickupOrderTypeCode;
    }

    if (this.state.session.data && this.state.session.data.id) {
      bodyPayload.session_id = this.state.session.data.id;
      bodyPayload.is_round = false;
    }
    return bodyPayload;
  },

  async placeRestaurantOrder({ commit, getters, rootGetters, dispatch }) {
    if (!(await dispatch("guardCartAvailability"))) {
      return { blocked: true };
    }
    const bodyPayload = {
      ...getters.getOrderPayloadWithoutPayment,
    };

    if (rootGetters["theme/isRestaurantEnabled"]) {
      bodyPayload["order_source_code"] =
        rootGetters["theme/general"].appSetting.restaurantOrderSourceCode;
      bodyPayload["order_type_code"] =
        rootGetters["theme/general"].appSetting.restaurantOrderTypeCode;
    } else {
      bodyPayload["order_source_code"] =
        rootGetters["theme/general"].appSetting.pickupOrderSourceCode;
      bodyPayload["order_type_code"] = rootGetters["theme/general"].appSetting.pickupOrderTypeCode;
    }

    if (this.state.session.data && this.state.session.data.id) {
      bodyPayload.session_id = this.state.session.data.id;
      bodyPayload.is_round = false;
    }
    try {
      commit("SET_ORDER_LOADING", true);
      const result = await cartService.createOrder(bodyPayload);
      commit("SET_ORDER_LOADING", false);
      commit("SET_ERROR", false);
      commit("SET_ERROR_DATA", {});
      if (result && result.data) {
        commit("SET_ORDER_DATA", result.data);
      }
      commit("CLEAR_CART");
      return result;
    } catch (e) {
      // A cancelled request means a newer order request superseded this one.
      // It is not an order failure, so no error state and no snackbar; the
      // newer request owns the outcome and clears the loading flag, which
      // also keeps the button disabled instead of inviting another tap.
      if (axios.isCancel(e)) {
        return null;
      }
      commit("SET_ERROR", true);
      commit("SET_ORDER_LOADING", false);
      console.error("cart: order request failed", e);
      const described = describeError(e);
      if (described.errors) {
        commit("SET_ERROR_DATA", described.errors);
      }
      commit("snackbar/error", errorMessageFor(described), {
        root: true,
      });
      return null;
    }
  },

  setTable({ commit }, tableId) {
    commit("SET_TABLE", tableId);
  },

  create({ commit }, payload) {
    commit("PUSH_DATA", payload);
  },

  addProduct({ commit }, payload) {
    commit("ADD_PRODUCT", payload);
  },

  deleteProduct({ commit }, payload) {
    commit("DELETE_PRODUCT", payload);
  },

  setPaymentMethod({ commit }, payload) {
    commit("SET_PAYMENT_METHOD", payload);
  },
};

const mutations = {
  SET_CUSTOMER_DATA(state, payload) {
    state.customer = payload;
  },

  SET_REQUESTED_TIME(state, datetime) {
    state.requested_time = datetime;
  },

  SET_PAYMENT_METHOD(state, payload) {
    state.paymentMethod = payload.code;
    state.paymentMethodOptions = payload.options;
  },
  SET_TABLE(state, tableId) {
    state.tableId = tableId;
  },
  SET_ORDER_DATA(state, order) {
    state.order = order;
    if (order && order.id) {
      state.orderId = order.id;
    }
  },
  SET_ORDER_ID(state, orderId) {
    state.orderId = orderId;
  },

  SET_DATA(state, data) {
    state.data = data;
  },

  SET_ERROR(state, data) {
    state.isError = data;
  },

  SET_ERROR_DATA(state, data) {
    state.errorData = data;
  },

  PUSH_DATA(state, data) {
    state.data = [...state.data, data];
  },

  ADD_PRODUCT(state, data) {
    state.products = [...state.products, data];
  },

  SET_BLOCKED_ARTICLES(state, blocked) {
    state.blockedArticles = blocked || NO_BLOCKED_ARTICLES;
  },

  REMOVE_BLOCKED_LINES(state) {
    state.products = state.products.filter(
      (line) => !isCartLineBlocked(line, state.blockedArticles)
    );
  },

  // By position: two lines can share an article id (same product, different
  // options), which DELETE_PRODUCT cannot tell apart.
  REMOVE_LINE(state, index) {
    if (index >= 0 && index < state.products.length) {
      state.products.splice(index, 1);
    }
  },

  DELETE_PRODUCT(state, payload) {
    const i = state.products.map((item) => item.id).indexOf(payload);
    state.products.splice(i, 1);
  },

  SET_ORDER_LOADING(state, payload) {
    state.isOrderLoading = payload;
  },

  CLEAR_CART(state) {
    state.data = {};
    state.products = [];
    state.blockedArticles = NO_BLOCKED_ARTICLES;
    state.paymentMethod = "";
    state.paymentMethodOptions = {};
  },
};

const getters = {
  getOrder(state) {
    return state.order;
  },

  // Indexes into state.products of the lines the last check flagged.
  blockedLineIndexes(state) {
    const indexes = [];
    (state.products || []).forEach((line, index) => {
      if (isCartLineBlocked(line, state.blockedArticles)) {
        indexes.push(index);
      }
    });
    return indexes;
  },

  hasBlockedLines(state, getters) {
    return getters.blockedLineIndexes.length > 0;
  },

  getTotalProduct(state) {
    let allProducts = [];
    let totalProduct = 0;
    if (state.products && state.products.length > 0) {
      allProducts = state.products.map((item) => item.quantity);
      totalProduct = allProducts.reduce((a, b) => a + b);
    }
    return totalProduct.toString();
  },
  tableId(state) {
    return state.tableId;
  },
  subTotal(state) {
    let total = 0;
    state.products.forEach((product) => {
      total += product.totalPrice * product.quantity;
    });
    return parseFloat(total).toFixed(2);
  },
  grandTotal(state, getters) {
    const total = getters.subTotal + state.deliveryCost;
    return parseFloat(total).toFixed(2);
  },
  getOrderPayload(state, getters, rootState) {
    const allProducts = [...state.products];
    const productList = [];

    allProducts.forEach((product) => {
      productList.push({
        ...product,
      });
      product.optionalGroups.forEach((optionalGroup) => {
        productList.push({
          ...optionalGroup,
          parent_id: product.id,
          quantity: product.quantity, //replace quantity with parent
        });
      });
    });

    const orderData = {
      ...state.data,
      table: rootState.session.data.table_detail.id,
      table_number: rootState.session.data.table_detail.number,
      shop_id: state.shopId,
      order_datetime: commonHelper.formatDateTime(commonHelper.getNowUTC()),
      requested_time: commonHelper.formatDateTime(commonHelper.getNowUTC()),
      payment_methods: [
        {
          code: state.paymentMethod ? state.paymentMethod : "cash",
          amount: getters.grandTotal,
          is_paid: true,
          data: {
            ...state.paymentMethodOptions,
            process: state.paymentMethod == "cash" ? false : true,
          },
        },
      ],
    };

    return new OrderJson(orderData, productList, {
      name: `QR APP #${rootState.session.data.table_detail.number}`,
    });
  },

  getOrderPayloadWithoutPayment(state, getters, rootState) {
    const allProducts = [...state.products];
    const productList = [];

    allProducts.forEach((product) => {
      productList.push({
        ...product,
      });
      product.optionalGroups.forEach((optionalGroup) => {
        productList.push({
          ...optionalGroup,
          parent_id: product.id,
          quantity: product.quantity, //replace quantity with parent
        });
      });
    });

    const orderData = {
      ...state.data,
      table: rootState.session.data.table_detail.id,
      table_number: rootState.session.data.table_detail.number,
      shop_id: state.shopId,
      order_datetime: commonHelper.formatDateTime(commonHelper.getNowUTC()),
      requested_time: commonHelper.formatDateTime(commonHelper.getNowUTC()),
      payment_methods: [],
    };

    return new OrderJson(orderData, productList, {
      name: `QR APP #${rootState.session.data.table_detail.number}`,
    });
  },

  generateOrderPayload(state, getters, rootState, rootGetters) {
    const bodyPayload = {
      ...getters.getOrderPayloadWithoutPayment,
    };
    if (rootGetters["theme/isRestaurantEnabled"]) {
      bodyPayload["order_source_code"] =
        rootGetters["theme/general"].appSetting.restaurantOrderSourceCode;
      bodyPayload["order_type_code"] =
        rootGetters["theme/general"].appSetting.restaurantOrderTypeCode;
    } else {
      bodyPayload["order_source_code"] =
        rootGetters["theme/general"].appSetting.pickupOrderSourceCode;
      bodyPayload["order_type_code"] = rootGetters["theme/general"].appSetting.pickupOrderTypeCode;
    }

    if (
      rootGetters["theme/isRestaurantEnabled"] &&
      rootState.session.data &&
      rootState.session.data.id
    ) {
      bodyPayload.session_id = rootState.session.data.id;
      bodyPayload.is_round = false;
    } else {
      bodyPayload.table_number = "";
    }

    if (state.customer) {
      bodyPayload["customer"] = state.customer;
    }

    if (state.requested_time && state.requested_time.date && state.requested_time.time) {
      bodyPayload["requested_time"] =
        state.requested_time.date + " " + state.requested_time.time + ":00";
    }

    if (state.paymentMethod) {
      let paymentLine = {
        code: state.paymentMethod,
        is_paid: state.paymentMethod == "cash" ? true : false,
        amount: getters.grandTotal,
      };
      if (JSON.stringify(state.paymentMethodOptions) != "{}") {
        paymentLine["data"] = state.paymentMethodOptions;
        if (state.paymentMethod == "ideal") {
          paymentLine["data"]["process"] = true;
        }
      }

      bodyPayload["payment_methods"] = [paymentLine];
    }

    return bodyPayload;
  },
};

const cart = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};

export default cart;
